import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowRightIcon,
  BookOpenIcon,
  DatabaseIcon,
  FileSearchIcon,
  FileTextIcon,
  FolderOpenIcon,
  LayoutDashboardIcon,
  MessageCircleIcon,
  ScaleIcon,
  ScanSearchIcon,
  ThumbsUpIcon,
  UploadIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useProductName } from "@/lib/branding";

// ---------------------------------------------------------------------------
// Capability definitions
// ---------------------------------------------------------------------------

interface Capability {
  id: string;
  title: string;
  icon: React.ElementType;
  color: string;
  summary: string;
  details: string[];
  linkTo: string;
  linkLabel: string;
}

const CAPABILITIES: Capability[] = [
  {
    id: "projects",
    title: "Upload & organize documents",
    icon: UploadIcon,
    color: "bg-emerald-100 text-emerald-700",
    summary:
      "Create projects to organize your documents. Upload files and they are automatically classified into the right folders.",
    details: [
      "Create projects for each deal, asset, or portfolio you're working on",
      "Upload documents in bulk -- PDFs, spreadsheets, Word docs, and more",
      "AI auto-classifies documents into the right folder categories",
      "Track document counts, upload dates, and processing status",
      "Group related projects into portfolios for aggregate views and comparisons",
    ],
    linkTo: "/projects/",
    linkLabel: "Go to Projects",
  },
  {
    id: "questions",
    title: "Define your question bank",
    icon: FileSearchIcon,
    color: "bg-blue-100 text-blue-700",
    summary:
      "Build a reusable library of due diligence questions. Each question defines what to look for, where to look, and how to score results.",
    details: [
      "Create questions organized by category (e.g. PPA, ITC, Interconnection)",
      "Each question has AI instructions for extracting answers from documents",
      "Reuse questions across multiple analyses and projects",
      "Extracted answers appear in each project's Answers tab",
      "Export question banks to share across your organization",
    ],
    linkTo: "/question-bank/",
    linkLabel: "Go to Question Bank",
  },
  {
    id: "analysis",
    title: "Run AI-powered analysis",
    icon: ScanSearchIcon,
    color: "bg-violet-100 text-violet-700",
    summary:
      "The Analysis Builder runs your question bank against project documents. The AI reads every document and extracts structured answers with source citations.",
    details: [
      "Select questions and target projects, then run the analysis",
      "AI reads all relevant documents and extracts answers",
      "Every answer includes source citations you can verify",
      "Generate reports comparing results across projects",
    ],
    linkTo: "/builder/",
    linkLabel: "Go to Analysis Builder",
  },
  {
    id: "feedback",
    title: "Review answers & give feedback",
    icon: ThumbsUpIcon,
    color: "bg-orange-100 text-orange-700",
    summary:
      "You stay in control of every AI answer. Verify citations, correct what the AI picked, and rate answers -- feedback is collected and turned into suggestions that improve your questions over time.",
    details: [
      "Give a thumbs up or down on any extracted answer, with an optional note",
      "Override which answer is used as the current value when the AI picks the wrong one",
      "Track review status so your team knows which answers have been checked",
      "Feedback is aggregated into suggested question improvements in the Question Bank",
    ],
    linkTo: "/question-bank/",
    linkLabel: "Go to Question Bank",
  },
  {
    id: "dashboards",
    title: "Build dashboards",
    icon: LayoutDashboardIcon,
    color: "bg-amber-100 text-amber-700",
    summary:
      "Create visual dashboards with charts, tables, and KPI widgets to track key metrics across your projects.",
    details: [
      "Widgets: bar charts, pie charts, line charts, tables, KPI cards, and text boxes",
      "Create custom dashboards to visualize your analysis data",
      "Save any dashboard as a template to standardize reporting across projects",
      "Share dashboards with your team",
    ],
    linkTo: "/dashboards/",
    linkLabel: "Go to Dashboards",
  },
  {
    id: "key-data",
    title: "Explore key data across projects",
    icon: DatabaseIcon,
    color: "bg-cyan-100 text-cyan-700",
    summary:
      "The Document Data Explorer is a cross-project view of extracted document data -- answer questions like \"which contracts expire this year?\" in seconds.",
    details: [
      "See document type, key dates, and counterparties for every document",
      "Review extracted signatures and key variables across projects",
      "Filter, sort, and export results to Excel",
      "Click any row to jump to the document in its project",
    ],
    linkTo: "/key-data/",
    linkLabel: "Go to Document Data Explorer",
  },
  {
    id: "chat",
    title: "Chat with your documents",
    icon: MessageCircleIcon,
    color: "bg-teal-100 text-teal-700",
    summary:
      "Ask free-form questions about any of your uploaded documents. The AI searches across your project files and answers with citations.",
    details: [
      "Ask questions in natural language about any uploaded document",
      "AI searches across your project files using vector search",
      "Every answer includes references to the source document and page",
      "Save conversations to come back to later",
      "Also available in Slack and Microsoft Teams when enabled for your organization",
    ],
    linkTo: "/chats",
    linkLabel: "Go to Chat",
  },
  {
    id: "rulesets",
    title: "Define rule sets",
    icon: ScaleIcon,
    color: "bg-rose-100 text-rose-700",
    summary:
      "Create rule sets that define compliance criteria or scoring logic. Apply them to analysis reports to flag issues automatically.",
    details: [
      "Define rules with conditions and thresholds",
      "Apply rules to analysis reports for automated compliance checking",
      "Track pass/fail status across projects",
    ],
    linkTo: "/rule-sets/",
    linkLabel: "Go to Rule Sets",
  },
  {
    id: "org-context",
    title: "Add organization context",
    icon: FolderOpenIcon,
    color: "bg-indigo-100 text-indigo-700",
    summary:
      "Upload reference documents that give the AI background context about your organization's standards, policies, or preferences. This context feeds into analysis to improve answer quality.",
    details: [
      "Upload internal guidelines, checklists, or reference materials",
      "Context documents are used by the AI during analysis",
      "Improves AI answer quality and alignment with your standards",
      "Organize context into collections by topic",
    ],
    linkTo: "/org-context/",
    linkLabel: "Go to Org Context",
  },
  {
    id: "reports",
    title: "Generate reports",
    icon: FileTextIcon,
    color: "bg-pink-100 text-pink-700",
    summary:
      "Create structured reports from the Analysis section. Reports summarize findings across all questions and projects in a shareable format.",
    details: [
      "Generate reports from completed analyses in Analysis Builder",
      "Reports include all extracted answers with source citations",
      "Build dashboards directly from report data",
      "Export or share reports with your team",
    ],
    linkTo: "/builder/",
    linkLabel: "Go to Analysis Builder",
  },
  {
    id: "playbooks",
    title: "Build playbooks",
    icon: BookOpenIcon,
    color: "bg-sky-100 text-sky-700",
    summary:
      "Operationalize your intelligence: create playbooks that capture how your team analyzes deals, then apply them to your extracted data (reports) via deep dives.",
    details: [
      "Playbooks capture the audience, tone, output format, and verification guidance",
      "Build playbooks from your own reference documents and standards",
      "Apply a playbook to a project's extracted data to generate a deep dive -- a long-form narrative report",
      "Every section is grounded in citations you can verify",
    ],
    linkTo: "/deep-dive-personas/",
    linkLabel: "Go to Playbooks",
  },
];

// ---------------------------------------------------------------------------
// Typical workflow steps
// ---------------------------------------------------------------------------

const WORKFLOW_STEPS = [
  {
    step: 1,
    label: "Create a project and upload documents",
    description: "Start by creating a project and uploading the relevant documents -- PPAs, interconnection agreements, environmental reports, etc.",
    linkTo: "/projects/",
  },
  {
    step: 2,
    label: "Set up your questions",
    description: "Build your question bank, or upload a key reference document to Org Context and let the AI generate questions and an analysis template from it.",
    linkTo: "/question-bank/",
  },
  {
    step: 3,
    label: "Run an analysis",
    description: "Select your questions and target projects, then let the AI extract structured answers from every document.",
    linkTo: "/builder/",
  },
  {
    step: 4,
    label: "Review results and build dashboards",
    description: "Review the AI's answers (each with source citations), generate reports, and build visual dashboards to track KPIs.",
    linkTo: "/dashboards/",
  },
  {
    step: 5,
    label: "Iterate and improve",
    description: "Correct answers and rate them with a thumbs up or down -- feedback becomes suggested question improvements. Add documents, re-run analyses, and compare across projects.",
    linkTo: "/builder/",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface GettingStartedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GettingStartedDialog({
  open,
  onOpenChange,
}: GettingStartedDialogProps) {
  const productName = useProductName();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">How {productName} works</DialogTitle>
          <DialogDescription>
            {productName} is an AI-powered platform for renewable energy due
            diligence. Upload documents, run analyses, and build dashboards --
            all in one place.
          </DialogDescription>
        </DialogHeader>

        {/* Typical workflow */}
        <div className="mt-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Typical workflow
          </h3>
          <ol className="space-y-3">
            {WORKFLOW_STEPS.map((ws) => (
              <li key={ws.step}>
                <Link
                  to={ws.linkTo}
                  className="flex gap-3 rounded-lg p-2 transition-colors hover:bg-gray-50"
                  onClick={() => onOpenChange(false)}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cf-foreground text-xs font-semibold text-white">
                    {ws.step}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {ws.label}
                    </p>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {ws.description}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        </div>

        {/* Capabilities accordion */}
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            What you can do
          </h3>
          <Accordion type="single" collapsible className="w-full">
            {CAPABILITIES.map((cap) => (
              <AccordionItem key={cap.id} value={cap.id}>
                <AccordionTrigger className="text-sm hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-lg ${cap.color}`}
                    >
                      <cap.icon className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{cap.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pl-11">
                  <p className="mb-2 text-sm text-muted-foreground">
                    {cap.summary}
                  </p>
                  <ul className="mb-3 space-y-1">
                    {cap.details.map((detail, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-xs text-muted-foreground"
                      >
                        <span className="mt-1 block h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={cap.linkTo}
                    className="inline-flex items-center gap-1 text-xs font-medium text-cf-foreground hover:underline"
                    onClick={() => onOpenChange(false)}
                  >
                    {cap.linkLabel}
                    <ArrowRightIcon className="h-3 w-3" />
                  </Link>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </DialogContent>
    </Dialog>
  );
}
